import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { MERCHANT_COMPANY_DOCUMENTS_PATH, getMerchantDefaultPath, merchantHasDepoType, merchantHasNakliyeType } from "./utils/merchantNavigation";
import { getAdminPages, getMerchantPages, getCustomerPages } from "./pageConfig";
import { useFrontAuth } from "./hooks/useFrontAuth";
import { useMerchantFrontAuth } from "./hooks/useMerchantFrontAuth";

// Geçerli path'i bilinen bir sayfaya eşle (param/nested path'leri base'e indirerek).
// Örn. "/admin-requests/123" -> admin-requests, "/troyi/conversations/9" -> troyi/conversations
function matchPage(pathname, pages) {
  let best = null;
  for (const page of pages) {
    if (pathname === page.path || pathname.startsWith(page.path + "/")) {
      if (!best || page.path.length > best.path.length) best = page;
    }
  }
  return best;
}

const ProtectedRoute = ({ element }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userType, isContractLocked, status, merchantCompanyTypes, merchantRole, isCustomerConsentLocked } = useAuth();
  const { canView, canManagePermissions } = useFrontAuth();
  const {
    canView: merchantCanView,
    canManagePermissions: merchantCanManagePermissions,
  } = useMerchantFrontAuth();
  const sessionToken = sessionStorage.getItem("token");
  const localToken = localStorage.getItem("token");
  const isAuthenticated = !!sessionToken || !!localToken;

  useEffect(() => {
    const createRequestMatch = location.pathname.match(/^\/create-request\/(?!form(?:\/|$))([^/]+)(?:\/(form))?$/);
    const hasDepoType = merchantHasDepoType(merchantCompanyTypes);
    const hasNakliyeType = merchantHasNakliyeType(merchantCompanyTypes);
    // Merchant yönlendirme fallback'i: /homepage artık merchant anasayfasını RENDER ettiği için
    // "yetkisiz sayfa" durumlarında kullanılacak gerçek sayfa doğrudan buradan çözülür.
    const merchantDefaultPath =
      getMerchantDefaultPath({ merchantRole, merchantCompanyTypes }) || "/homepage";
    const merchantSafeFallbackPath =
      merchantDefaultPath && merchantDefaultPath !== location.pathname ? merchantDefaultPath : "/homepage";

    if (!isAuthenticated) {
      navigate("/");
      return;
    }

    // Merchant: sözleşme + evraklar onaylanmadan Operasyonlarım görünmesin
    if (
      status === "ready" &&
      userType === "merchant" &&
      isContractLocked &&
      (
        location.pathname === "/my-operations" ||
        location.pathname.startsWith("/my-operations/") ||
        location.pathname === "/transport-requests" ||
        location.pathname.startsWith("/transport-requests/")
      )
    ) {
      navigate(merchantRole === "admin" ? MERCHANT_COMPANY_DOCUMENTS_PATH : merchantSafeFallbackPath, { replace: true });
      return;
    }

    if (status === "ready" && location.pathname.startsWith("/troyi") && userType !== "admin") {
      navigate("/homepage", { replace: true });
      return;
    }

    if (status === "ready" && location.pathname.startsWith("/overview") && userType !== "admin") {
      navigate("/homepage", { replace: true });
      return;
    }

    // Partner Genel Bakış yalnız iş ortağı içindir (sayfa yetkisi aşağıdaki merchant kapısında).
    if (status === "ready" && location.pathname === "/merchant-overview" && userType !== "merchant") {
      navigate("/homepage", { replace: true });
      return;
    }

    // frontAuth: admin alt-rol (superadmin/admin/customer-relations) yetkisine göre
    // admin sayfalarına erişim kontrolü. Yetkisi yoksa ilk izinli admin sayfasına yönlendir.
    if (status === "ready" && userType === "admin") {
      // /customer-contracts/:customerID, ContractsPage'in aynısını açar ama pageConfig'teki
      // "/contracts" yoluna eşleşmez; sözleşme yetkisi kapalı bir admin bu URL'den içeriği
      // görebiliyordu. Eşleme öncesinde yolu bilinen sayfaya indiriyoruz.
      const normalizedPath = location.pathname.startsWith("/customer-contracts")
        ? "/contracts"
        : location.pathname;
      const page = matchPage(normalizedPath, getAdminPages());
      if (page) {
        const allowed =
          page.key === "user-permissions" ? canManagePermissions : canView(page.key);
        if (!allowed) {
          const fallback = getAdminPages().find((p) => canView(p.key));
          navigate(fallback ? fallback.path : "/homepage", { replace: true });
          return;
        }
      }
    }

    // Zorunlu onay kilidi (müşteri + merchant): KVKK / Kullanıcı Sözleşmesi (güncel sürüm)
    // onaylanmadan hiçbir sayfada işlem yapılamaz — her rota anasayfaya döner, onaylar orada verilir.
    if (status === "ready" && isCustomerConsentLocked && location.pathname !== "/homepage") {
      navigate("/homepage", { replace: true });
      return;
    }

    // merchant frontAuth: sayfa/aksiyon/kapsam yetkisine göre merchant sayfalarına erişim.
    // Yetkisi yoksa ilk izinli merchant sayfasına yönlendir (yoksa güvenli fallback'e).
    // Sözleşme kilidi (isContractLocked) yetki kapısını DEVRE DIŞI BIRAKMAZ: kilit yalnız
    // hangi sayfaların açık kalacağını daraltır, rol yetkisi her hâlükârda uygulanır.
    if (status === "ready" && userType === "merchant") {
      const merchantPages = getMerchantPages();
      const isMerchantPageAllowed = (p) =>
        p.key === "homepage" || p.key === "merchant-information" || p.key === "ask-troyi" || p.key === "faq" || p.key === "recommend-customer" || p.key === "store" ? true // anasayfa + bilgi + TROYİ + S.S.S + Müşteri Öner: tüm merchant rollerine açık (Müşteri Öner artık firma tipiyle sınırlı değil)
          // "Şirket Bilgilerim" YETKİ TABANLI kalır: legacy/rolsüz "member" bu yetkiye sahip
          // olduğu için kilidi oradan açabilir; ŞOFÖR gibi bu yetkisi olmayan alt-roller
          // kilitliyken de (evrak eksik) firma profiline giremez — koşulsuz açmak onları da
          // içeri alıyordu.
          : p.key === "user-permissions" ? merchantCanManagePermissions
          : merchantCanView(p.key);
      const merchantNormalizedPath = location.pathname.startsWith("/customer-contracts")
        ? "/contracts"
        : location.pathname;
      const page = matchPage(merchantNormalizedPath, merchantPages);
      if (page && !isMerchantPageAllowed(page)) {
        // Döngüyü önlemek için merchantSafeFallbackPath'e değil, izinli bir sayfaya yönlen.
        const fallback = merchantPages.find(isMerchantPageAllowed);
        navigate(fallback ? fallback.path : merchantSafeFallbackPath, { replace: true });
        return;
      }
    }

    if (status === "ready" && location.pathname.startsWith("/chat") && !["admin", "merchant", "customer"].includes(userType)) {
      navigate("/homepage", { replace: true });
      return;
    }

    // 2. El Mağaza müşteri ve iş ortağı içindir. Yalnız /store ve /store/... — "/store-settings"
    // (admin Mağaza Ayarları) gibi aynı önekle başlayan admin sayfaları bu kurala takılmasın.
    const isStorePath = location.pathname === "/store" || location.pathname.startsWith("/store/");
    if (status === "ready" && isStorePath && !["merchant", "customer"].includes(userType)) {
      navigate("/homepage", { replace: true });
      return;
    }

    // TROYİ asistanı müşteri ve iş ortağı içindir (adminlerin kendi Troyi paneli var).
    if (status === "ready" && location.pathname === "/ask-troyi" && !["merchant", "customer"].includes(userType)) {
      navigate("/homepage", { replace: true });
      return;
    }

    if (status === "ready" && userType === "merchant" && location.pathname === "/my-customers") {
      if (!hasDepoType) {
        navigate(merchantSafeFallbackPath, { replace: true });
        return;
      }
    }

    if (status === "ready" && userType === "merchant" && location.pathname === "/warehouse-list") {
      if (!hasDepoType) {
        navigate(merchantSafeFallbackPath, { replace: true });
        return;
      }
    }

    if (status === "ready" && location.pathname === "/my-warehouses" && userType !== "customer") {
      navigate("/homepage", { replace: true });
      return;
    }

    // /my-warehouses/:userID admin veya depo firması merchant'ının müşteri depo bilgisi görüntülemesi içindir
    if (status === "ready" && /^\/my-warehouses\/[^/]+/.test(location.pathname) && userType !== "admin" && userType !== "merchant") {
      navigate("/homepage", { replace: true });
      return;
    }

    if (status === "ready" && location.pathname === "/my-requests" && userType !== "customer") {
      navigate("/homepage", { replace: true });
      return;
    }

    if (status === "ready" && location.pathname === "/my-offers" && userType !== "customer") {
      navigate("/homepage", { replace: true });
      return;
    }

    if (status === "ready" && location.pathname === "/my-contracts" && userType !== "customer") {
      navigate("/homepage", { replace: true });
      return;
    }

    if (status === "ready" && location.pathname === "/my-payments" && userType !== "customer") {
      navigate("/homepage", { replace: true });
      return;
    }

    // Ödeme dönüş sayfası (3DS callback) yalnız müşteri içindir.
    if (status === "ready" && location.pathname === "/payment-result" && userType !== "customer") {
      navigate("/homepage", { replace: true });
      return;
    }

    // /payment-methods ARTIK KULLANILMIYOR: sayfa hiçbir servis çağırmıyor, kod içine gömülü
    // SAHTE kart listesi gösteriyor ve "Kartı Kaydet" hiçbir şey yapmıyordu. Gerçek özellik
    // Ödemelerim > "Kayıtlı Kartlarım" (SavedCardsModal). Eski link/yer imleri oraya yönlenir.
    if (status === "ready" && location.pathname === "/payment-methods") {
      navigate(userType === "customer" ? "/my-payments" : "/homepage", {
        replace: true,
        state: userType === "customer" ? { openCards: true } : undefined,
      });
      return;
    }

    // /payments admin ve merchant içindir; müşteri kendi carisine yönlendirilir.
    if (status === "ready" && location.pathname.startsWith("/payments") && userType === "customer") {
      navigate("/my-payments", { replace: true });
      return;
    }

    // /contracts admin ve merchant içindir; müşteri kendi sayfasına yönlendirilir.
    if (status === "ready" && location.pathname === "/contracts" && userType === "customer") {
      navigate("/my-contracts", { replace: true });
      return;
    }

    if (status === "ready" && location.pathname.startsWith("/customer-contracts") && userType === "customer") {
      navigate("/my-contracts", { replace: true });
      return;
    }

    if (
      status === "ready" &&
      userType === "merchant" &&
      location.pathname.startsWith("/customer-contracts") &&
      !hasDepoType
    ) {
      navigate(merchantSafeFallbackPath, { replace: true });
      return;
    }

    if (status === "ready" && location.pathname === "/admin-offers" && userType === "customer") {
      navigate(`/my-offers${location.search || ""}`, { replace: true });
      return;
    }

    if (status === "ready" && createRequestMatch && userType === "customer") {
      const isFormRoute = createRequestMatch[2] === "form";
      navigate(isFormRoute ? "/create-request/form" : "/create-request", { replace: true });
      return;
    }

    if (
      status === "ready" &&
      userType === "merchant" &&
      (location.pathname === "/transport-requests" || location.pathname.startsWith("/transport-requests/"))
    ) {
      if (!hasNakliyeType) {
        navigate(merchantSafeFallbackPath, { replace: true });
        return;
      }
    }

    // Öneri sayfası TEK rota, İKİ bağlam:
    //   merchant -> "Müşteri Öner" (affiliate programı; tüm firma tipleri)
    //   customer -> "Sevdiklerine Öner" (tavsiye)
    // Admin'e kapalı: kaydı zaten Kullanıcılar ekranından açıyor.
    if (status === "ready" && location.pathname === "/recommend-customer") {
      const allowed = userType === "merchant" || userType === "customer";
      if (!allowed) {
        navigate(userType === "merchant" ? merchantSafeFallbackPath : "/homepage", { replace: true });
        return;
      }
    }

    // SON KAPI — ADMIN'E ÖZEL ROTALAR: admin sayfa listesinde olup kullanıcının KENDİ rol
    // listesinde OLMAYAN her yol yalnız Troy Depo yöneticilerinindir. Önceden yalnız /troyi
    // ve /overview elle engelleniyordu; Kullanıcılar, Firmalar, Talepler, Teklifler gibi
    // sayfalar merchant ve müşteriye URL yazarak tamamen açıktı. Yukarıdaki özel
    // yönlendirmelerden (ör. /admin-offers → /my-offers) sonra çalışır ki onların hedefleri
    // korunsun; buraya yalnız kuralı olmayan admin sayfaları düşer.
    if (status === "ready" && userType && userType !== "admin") {
      const adminOnlyPage = matchPage(location.pathname, getAdminPages());
      if (adminOnlyPage) {
        // Kapı ROLE ÖZEL: kullanıcının KENDİ sayfa listesine bakılır. Birleşik (merchant VEYA
        // müşteri) kontrolünde, hem admin hem merchant listesinde olan /my-customers gibi
        // yollar müşteriye de açık kalıyordu.
        const roleMatch = userType === "merchant"
          ? matchPage(location.pathname, getMerchantPages())
          : matchPage(location.pathname, getCustomerPages());
        // İstisna: /my-warehouses/:userID müşteri depo bilgisidir ve yukarıdaki özel kuralla
        // depo firmasına açıktır; merchant sayfa listesinde olmadığı için burada elenmesin.
        const isMerchantCustomerWarehouseView =
          userType === "merchant" && /^\/my-warehouses\/[^/]+/.test(location.pathname);
        if (!roleMatch && !isMerchantCustomerWarehouseView) {
          navigate(userType === "merchant" ? merchantSafeFallbackPath : "/homepage", { replace: true });
          return;
        }
      }
    }
  }, [isAuthenticated, navigate, userType, isContractLocked, isCustomerConsentLocked, location.pathname, location.search, status, merchantCompanyTypes, merchantRole, canView, canManagePermissions, merchantCanView, merchantCanManagePermissions]);

  return element;
};

export default ProtectedRoute;
